import type { AuthTokens, AuthUser, LoginResponse } from "@tanzeel/shared";
import { API_BASE_URL, ApiError } from "./http";

/**
 * Token storage and rotation — SPEC §9.
 *
 * Tokens live in localStorage. Refresh is single-flight: several requests
 * expiring at once share one refresh call, so a burst of 401s cannot fire a
 * burst of rotations and invalidate each other's freshly-issued token.
 */

const ACCESS_KEY = "tanzeel.access";
const REFRESH_KEY = "tanzeel.refresh";
const USER_KEY = "tanzeel.user";

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    // Corrupt entry is equivalent to being logged out.
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

export function storeSession(session: LoginResponse): void {
  localStorage.setItem(ACCESS_KEY, session.tokens.accessToken);
  localStorage.setItem(REFRESH_KEY, session.tokens.refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Shared in-flight refresh — the single-flight dedupe the spec asks for. */
let inFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) throw new ApiError(401, "NO_SESSION", "Not signed in", null);

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const envelope = (await response.json()) as {
    success: boolean;
    data: LoginResponse | null;
    error: { code: string; message: string } | null;
  };

  if (!response.ok || !envelope.success || envelope.data === null) {
    clearSession();
    throw new ApiError(
      response.status,
      envelope.error?.code ?? "REFRESH_FAILED",
      envelope.error?.message ?? "Session expired",
      null,
    );
  }

  storeSession(envelope.data);
  return envelope.data.tokens.accessToken;
}

export function refreshAccessToken(): Promise<string> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export async function login(
  identifier: string,
  password: string,
): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ identifier, password }),
  });
  const envelope = (await response.json()) as {
    success: boolean;
    data: LoginResponse | null;
    error: { code: string; message: string } | null;
  };
  if (!response.ok || !envelope.success || envelope.data === null) {
    throw new ApiError(
      response.status,
      envelope.error?.code ?? "LOGIN_FAILED",
      envelope.error?.message ?? "Could not sign in",
      null,
    );
  }
  storeSession(envelope.data);
  return envelope.data;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken === null
          ? {}
          : { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Offline logout still clears local state; the tokens expire on their own.
  }
  clearSession();
}

export type { AuthTokens, AuthUser };
